import incommIcon from "assets/barcode-incomm-icon.png";
import { BALANCE_NAMESPACE, BARCODE_VIEW } from "constants/i18n-namespaces";
import { PayableCategory } from "constants/payable";
import { useCurrencyFormatter, useLabelsWithKrn } from "hooks";
import { Skeleton } from "kubra-ux-forge";
import _ from "lodash";
import { useBarcode } from "next-barcode";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getPayableSession } from "selectors/payableSession";
import { OrderPayable } from "types/payable";
import { getLabelWithKrn } from "utilities/helper";

export interface IBarcodeViewProps {
  barcodeText: string;
  billerName: string;
  billerLogo: string;
  accountNumber: string;
  serviceFee: number;
  payableTypeNameLabel?: string;
  payableAmount?: number;
  payableCategory?: string | undefined;
  selectedAmountOptionLabel?: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const BarcodeView = (props: IBarcodeViewProps) => {
  const {
    barcodeText,
    billerLogo,
    accountNumber,
    serviceFee,
    payableTypeNameLabel,
    payableAmount,
    payableCategory,
    selectedAmountOptionLabel,
  } = props;
  const { t } = useTranslation([BARCODE_VIEW, BALANCE_NAMESPACE]);
  const currencyFormatter = useCurrencyFormatter();
  const payableSession = useSelector(getPayableSession);
  const { getLabelsWithKrn } = useLabelsWithKrn();
  const labelSourceWithKrn = getLabelsWithKrn();
  const payable = payableSession?.payable;
  const accountOrQuantity =
    payable?.category?.toLowerCase() === "order"
      ? (payable as OrderPayable)?.quantity
      : accountNumber;
  const isMultiBusinessUnitSupported =
    (payableSession?.payableType?.supportedBusinessUnitCodes?.length ?? 0) > 1;
  const shouldShowBusinessUnit =
    isMultiBusinessUnitSupported &&
    (payable?.category?.toLowerCase() === "order" ||
      payable?.category?.toLowerCase() === "freeform") &&
    !_.isEmpty(payable?.businessUnitCode);
  const payableTypeKrn =
    payable?.typeKrn ?? payableSession?.payableType?.typeKrn ?? null;
  const resolvedBusinessUnitCode =
    payableSession?.payableType?.supportedBusinessUnitCodes?.find(
      (code) =>
        code.toLowerCase() === (payable?.businessUnitCode ?? "").toLowerCase()
    ) ?? payable?.businessUnitCode ?? "";
  const resolvedBusinessUnitLabel = getLabelWithKrn(
    labelSourceWithKrn.labelsWithKrn,
    payableTypeKrn,
    "businessUnitType"
  );
  const businessUnitLabel =
    resolvedBusinessUnitLabel === "businessUnitType"
      ? t("location-label")
      : resolvedBusinessUnitLabel;
  const resolvedBusinessUnitValue = getLabelWithKrn(
    labelSourceWithKrn.labelsWithKrn,
    payableTypeKrn,
    resolvedBusinessUnitCode
  );


  // Display Servicefee text if enabled and > 0
  let serviceFeeDisplay = "";
  if (serviceFee !== undefined && serviceFee > 0.0) {
    serviceFeeDisplay = t("service-fee-label", {
      serviceFee: currencyFormatter.format(serviceFee),
    });
  }

  //Set barcode properties
  const { inputRef } =
    useBarcode({
      value: barcodeText,
      options: {
        displayValue: true,
        background: "#ffffff",
        fontSize: 22,
      },
    }) || {};

  const isInit = barcodeText === "init";

  return (
    <div
      className="barcode-view"
      id="barcodeview"
      title={`${t("barcode-view-title")}`}
    >
      <div className="cash-account-logo-container">
        <div className="cash-biller-logo-container">
          <img
            src={billerLogo}
            alt={`${t("biller-logo-alt", { billerName: props.billerName })}`}
            className="biller-logo-cash"
            data-cy="biller-logo"
          />
        </div>
        {_.isEmpty(payableTypeNameLabel) && (
          <div className="cash-account-number">
            <div className="account-display-cash" data-cy="barcode-account-number">
              <b data-cy="barcode-account-number-label">{t("account-number")}</b> <br /> <span data-cy="barcode-account-number-value">{accountOrQuantity}</span>
            </div>
          </div>
        )}

        {!_.isEmpty(payableTypeNameLabel) && (
          <div className="cash-account-details">
            {shouldShowBusinessUnit && (
              <div className="cash-account-number">
                <div className="account-display-cash" data-cy="business-unit">
                  <b>{businessUnitLabel}:</b> {resolvedBusinessUnitValue}
                </div>
              </div>
            )}
            <div className="cash-account-number">
              <div className="account-display-cash" data-cy="barcode-account-number">
                {selectedAmountOptionLabel ? (
                  <>
                    <b data-cy="barcode-account-number-label">
                      {getLabelWithKrn(
                        labelSourceWithKrn.labelsWithKrn,
                        payableTypeKrn,
                        "invoiceNumber"
                      )}
                      :
                    </b>{" "}
                    <span data-cy="barcode-account-number-value">
                      {accountOrQuantity}
                    </span>
                  </>
                ) : payableCategory &&
                (payableCategory === PayableCategory.ACCOUNT ||
                  payableCategory === PayableCategory.INVOICE) ? (
                  <>
                    <b data-cy="barcode-account-number-label">
                      {t("account-number-payable-type-name", {
                        payableTypeName: payableTypeNameLabel,
                      })}
                    </b>{" "}
                    <span data-cy="barcode-account-number-value">
                      {accountOrQuantity}
                    </span>
                  </>
                ) : (
                  <b data-cy="barcode-account-number-label">
                    {payableCategory === PayableCategory.ORDER
                      ? `${payableTypeNameLabel} (${accountOrQuantity})`
                      : payableTypeNameLabel}
                  </b>
                )}
              </div>
            </div>
            {payableSession?.payable?.category?.toLowerCase() !==
              PayableCategory.ACCOUNT && (
              <div className="cash-account-number">
                <div className="account-display-cash" data-cy="barcode-amount">
                  <b data-cy="barcode-amount-label">
                    {t("amount", { ns: BALANCE_NAMESPACE })}:
                  </b>{" "}
                  <span data-cy="barcode-amount-value">
                    {currencyFormatter.format(payableAmount ?? 0.0)}
                  </span>
                </div>
              </div>
            )}
            {selectedAmountOptionLabel && (
              <div className="cash-account-number">
                <div className="account-display-cash" data-cy="barcode-amount-option">
                  <b>{selectedAmountOptionLabel}</b>
                </div>
              </div>
            )}
          </div>
        )}
        {/* <div style={{ width: "100%", textAlign: "right", marginTop: "0px",  }}>parking ticket</div>
        <div style={{ width: "100%", textAlign: "right", marginTop: "8px" }}>Amount</div> */}
      </div>

      <div className="barcode-container">
        <div className="barcode-column">
          {isInit ? (
            <div
              className="barcode-skeleton-container"
              aria-label="barcode-skeleton"
            >
              <Skeleton shape="rectangle" />
            </div>
          ) : (
            <img src={incommIcon} alt="Incomm icon" className="incom-icon" />
          )}
          <canvas
            ref={inputRef}
            className={`barcode ${isInit ? "barcode--hidden" : ""}`}
            title={`${t("cash-barcode-title")}`}
            data-cy="cash-barcode"
          />
        </div>
      </div>
      <div className="content-row">
        <div className="left-text-14" data-cy="link-how-to-use-barcode">
          {t("how-to-use-barcode", { serviceFee: serviceFeeDisplay })}
        </div>
      </div>
      <div className="content-row">
        <div
          className="left-text-10"
          data-cy="barcode-disclaimer"
          dangerouslySetInnerHTML={{ __html: t("barcode-disclaimer") }}
        />
      </div>
    </div>
  );
};
